// Which failures are worth recording, and what the visitor is told about them.
//
// Both decisions are pure functions of the status, kept away from the hooks
// that use them so they can be exercised without a framework, a browser or a
// tracker behind them — and so the two hooks (server and browser) cannot drift
// apart on what counts as a failure.

// Below this a response is an answer, not a failure. The framework already
// filters most of it out for us: an `error(403, …)` raised deliberately never
// reaches `handleError` at all, because the framework treats it as the response
// it is. What does arrive with a sub-500 status is a request for a page that
// does not exist, and a 404 is not something anybody needs to be paged about —
// recording it would bury the one failure that matters under a stream of
// crawlers and stale links.
const SERVER_FAILURE_STATUS = 500;

// A request the client gave up on. The cabinet holds a long-lived stream open
// for live account events, and the browser aborts it every time the visitor
// navigates away, closes the tab or reloads — which is not a failure at all,
// but it surfaces to the server as a request that threw. Recording those means
// one event per page a visitor leaves: the tracker fills with the most ordinary
// thing a user does, and the one event that means a bug is buried under it.
//
// Matched on the error rather than on the status because the framework has no
// status for "the other end went away": it is a 500 by default, and only the
// error itself says otherwise.
function isClientGaveUp(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const name = (error as { name?: unknown }).name;
  if (name === "AbortError" || name === "ResponseAborted") return true;
  const code = (error as { code?: unknown }).code;
  if (code === "ABORT_ERR" || code === "ECONNRESET" || code === "EPIPE") return true;
  const cause = (error as { cause?: unknown }).cause;
  return cause !== undefined && cause !== error ? isClientGaveUp(cause) : false;
}

// Whether a failure that arrived on `window` — rather than through the
// framework — is worth recording.
//
// The `error` event also fires for things that carry nothing actionable: a
// script an extension blocked, a cross-origin failure the browser refuses to
// describe, an image that did not load. Each would file a stackless "Script
// error" that nobody can act on, and each costs a tracker round trip to do it.
// A rejection has the same problem plus the abort case, which is the browser
// cancelling work the visitor navigated away from — the most ordinary thing
// there is.
//
// So the rule is: a real thrown Error, and not one that says the client gave
// up. It is the same rule the rest of the app follows — decide before sending,
// not after.
export function shouldReportWindowFailure(value: unknown): boolean {
  if (!(value instanceof Error)) return false;
  return !isClientGaveUp(value);
}

export function shouldReport(status: number, error?: unknown): boolean {
  if (error !== undefined && isClientGaveUp(error)) return false;
  return status >= SERVER_FAILURE_STATUS;
}

// What the visitor reads. Never the thrown error's own text: a message that
// escaped a server-side failure is written for whoever is reading the stack,
// and it can carry a connection string, a path or a field of a document. The
// identifier of the recorded failure is what connects this page to that text,
// and it is safe to show because it means nothing without the tracker.
export function friendlyMessage(status: number): string {
  if (status >= SERVER_FAILURE_STATUS) {
    return "Something went wrong on our side. Nothing you did caused it, and we have recorded the failure.";
  }
  if (status === 404) {
    return "We could not find that page. It may have moved, or the link that brought you here may be out of date.";
  }
  if (status === 401) {
    return "You need to be signed in to see that page.";
  }
  if (status === 403) {
    return "That page is not available to this account.";
  }
  return "That request could not be completed.";
}

// The short line above the sentence. Kept separate so the page has a heading
// that reads as a statement rather than a status code.
export function friendlyTitle(status: number): string {
  if (status >= SERVER_FAILURE_STATUS) return "Something went wrong";
  if (status === 404) return "Page not found";
  if (status === 401 || status === 403) return "Not available";
  return "That did not work";
}
